import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors, TailwindColorValue } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column, Row } from '@repo/ui/data-table'
import {
  BellIcon,
  BuildingPlusIcon,
  CheckListTwoIcon,
  CreditCardIcon,
  EmailIcon,
  PharmacyIcon,
  StarFilledIcon,
  UserCanceledIcon
} from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { xor } from 'lodash'
import { useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { affectEnrollments } from '../../../api/enrollments/affect-enrollments'
import { cancelEnrollment } from '../../../api/enrollments/cancel-enrollment'
import { sendEmailToEnrollments } from '../../../api/enrollments/send-email-to-enrollments'
import {
  sendClientSpaceEmailReminder,
  sendClientSpacePhoneReminder
} from '../../../api/enrollments/send-reminders'
import CountryFlag from '../../../components/elements/country-flag'
import CseIdentifierTooltip from '../../../components/elements/cse-identifier-tooltip'
import FilterDrawer from '../../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import { countFilters } from '../../../components/elements/dynamic-table/utils/countFilters'
import InformationToolTip from '../../../components/elements/information-tooltip'
import UserNavBar from '../../../components/elements/nav-bar'
import SendEmailForm from '../../../components/pages/candidates/send-email-form'
import AffectEnrollmentsForm from '../../../components/pages/centers/affect-enrollments-form'
import { CancelEnrollmentNotificationModal } from '../../../components/pages/enrollments/cancel-enrollment-notification-modal'
import { CancelEnrollmentForm } from '../../../components/pages/enrollments/enrollment-form/cancel-enrollment-form'
import { EnrollmentsCenterFilter } from '../../../components/pages/enrollments/enrollments-center-filter'
import RemindersHistory from '../../../components/pages/enrollments/reminders-history'
import { Paths } from '../../../lib/constants/path'
import { useFilters } from '../../../lib/hooks/use-filters'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { genderOneLetterMapping } from '../../../lib/types/enums/common/gender.enum'
import { schoolLevelEnumMapping } from '../../../lib/types/enums/common/school-level.enum'
import {
  ApplicationStatusEnum,
  applicationStatusEnumMapping
} from '../../../lib/types/enums/enrollment/application-status.enum'
import { MedicalConditionEnum } from '../../../lib/types/enums/enrollment/medical-condition-enum'
import { PaymentStatusEnum } from '../../../lib/types/enums/enrollment/payment-status.enum'
import {
  enrollmentValidatedListFields,
  EnrollmentValidatedListType
} from '../../../lib/types/fields/enrollments/enrollment'
import { AffectEnrollmentFailure } from '../../../lib/types/models/enrollment/affect-enrollment'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import { EnrollmentFilter } from '../../../lib/types/models/enrollment/enrollment-filter'
import { TripLabelInput, TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformEnrollmentsFiltersToUrl,
  transformUrlToEnrollmentFilters
} from '../../../lib/utils/filters-mapping/enrollment'
import {
  extractAffectEnrollmentFailures,
  extractAllErrorMessages,
  showAxiosError
} from '../../../lib/utils/functions/misc.functions'
import EnrollmentIconsStatus from './enrollment-icon-status'

const defaultStatuses = [ApplicationStatusEnum.CONFIRMED, ApplicationStatusEnum.AFFECTED]

// Route on the first line, transport means on the second, to keep the column narrow
const TripLabelCell = ({ trip, disabled }: { trip?: TripLabelInput; disabled: boolean }) => {
  if (!trip)
    return (
      <Typography variant="secondary-body" disabled={disabled}>
        -
      </Typography>
    )

  const route = TripProperties.getTripLightLabel(trip)
  return (
    <div className="flex flex-col">
      {route && (
        <Typography variant="secondary-body" disabled={disabled}>
          {route}
        </Typography>
      )}
      <Typography variant="secondary-body" disabled={disabled}>
        {TripProperties.handleTransportMeansName(trip.transportMeans)}
      </Typography>
    </div>
  )
}

const EnrollmentsValidatedListPage = () => {
  const { t } = useTranslate()
  const queryClient = useQueryClient()
  const [isTableLoading, setIsTableLoading] = useState(false)

  const { navigateWithEvent } = useNavigator()
  const tableRef = useRef<DynamicTableRef<EnrollmentValidatedListType>>(null)

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'filters' | 'send-email' | 'affectation' | 'confirm-email-reminder',
    EnrollmentValidatedListType[]
  >(['filters', 'send-email', 'affectation', 'confirm-email-reminder'])
  // a new array on every render would re-initialize the popup's centre choices while it submits
  const affectationEnrollmentIds = useMemo(
    () => selectedData?.map((row) => row.id) ?? [],
    [selectedData]
  )

  const {
    modalState: cancelModalState,
    selectedData: cancelSelectedEnrollment,
    toggleModal: toggleCancelModal,
    closeModel: closeCancelModel
  } = useModalManager<'cancel-enrollment' | 'notification', EnrollmentValidatedListType>([
    'cancel-enrollment',
    'notification'
  ])

  const {
    modalState: reminderModalState,
    selectedData: reminderSelectedData,
    toggleModal: reminderToggleModal,
    closeModel: reminderCloseModel
  } = useModalManager<'reminder', number>(['reminder'])

  const { mutate: sendEmailToEnrollmentsMutation } = useMutation({
    mutationFn: sendEmailToEnrollments,
    onSuccess: () => {
      toast.success('Les emails ont été envoyés avec succès')
      closeModel('send-email')
    },
    onError: showAxiosError
  })

  // the students the server refused to place, kept in the popup with the reason
  const [affectationFailures, setAffectationFailures] = useState<AffectEnrollmentFailure[]>([])
  const closeAffectationModal = () => {
    setAffectationFailures([])
    closeModel('affectation')
  }

  const { mutate: affectEnrollmentsMutation, isPending: isAffectEnrollmentsPending } = useMutation(
    {
      mutationFn: affectEnrollments,
      onMutate: () => setAffectationFailures([]),
      onError: (error) => {
        const failures = extractAffectEnrollmentFailures(error)
        if (!failures.length) return showAxiosError(error)
        extractAllErrorMessages(error).forEach((message) => toast.error(message))
        setAffectationFailures(failures)
      },
      onSuccess: ({ message, failures }) => {
        tableRef.current?.refetch()
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.includes('trip-date-enrollments')
        })
        if (!failures.length) {
          toast.success(message)
          closeAffectationModal()
          return
        }
        // the placed students now show as affected in the popup, the refused ones keep their reason
        toast.warning(message)
        queryClient.invalidateQueries({ queryKey: ['enrollments', affectationEnrollmentIds] })
        // the centres' free places changed with the students just placed
        queryClient.invalidateQueries({ queryKey: ['centers'] })
        setAffectationFailures(failures)
      }
    }
  )

  const { mutate: cancelEnrollmentMutation, isPending: isCancelEnrollmentPending } = useMutation({
    mutationFn: cancelEnrollment,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Inscription annulée')
      closeCancelModel('cancel-enrollment')
      toggleCancelModal('notification', cancelSelectedEnrollment)
    }
  })

  const sendClientSpaceEmailReminderMutation = useMutation({
    mutationFn: sendClientSpaceEmailReminder,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success("Les relances par email de l'espace client ont été envoyées avec succès")
      tableRef.current?.refetch()
    }
  })

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<EnrollmentFilter>({
      initialFilters: {
        statuses: defaultStatuses
      },
      options: { allowSearchParam: true },
      transformFilterToUrl: transformEnrollmentsFiltersToUrl,
      transformUrlToFilter: transformUrlToEnrollmentFilters
    })

  const isDisabled = (row: Row<EnrollmentValidatedListType>) =>
    row.original.status === ApplicationStatusEnum.CANCELLED_AFTER_CONFIRMATION

  const openCancelEnrollmentModal = (enrollments: EnrollmentValidatedListType[]) => {
    const enrollment = enrollments[0]
    if (!enrollment || enrollments.length !== 1 || EnrollmentProperties.isCancelled(enrollment)) {
      toast.info("Sélectionne un seul bulletin validé pour l'annulation.")
      return
    }

    toggleCancelModal('cancel-enrollment', enrollment)
  }

  const columns: Column<EnrollmentValidatedListType>[] = [
    {
      accessorKey: 'enrollmentNumber',
      header: `N° d'inscription`,
      enableSorting: true,
      cell: ({ row }) => (
        <Typography variant="secondary-body" disabled={isDisabled(row)}>
          {row.original.enrollmentNumber}
        </Typography>
      )
    },
    {
      accessorKey: 'childLastName',
      header: `NOM Prénom`,
      enableSorting: true,
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-1">
            <Typography
              variant="secondary-body"
              className="text-grey text-balance w-full"
              disabled={isDisabled(row)}
            >
              <b>
                {toFullName(row.original.childFirstName, row.original.childLastName, {
                  options: { mode: 'lastname-firstname' }
                })}
              </b>
            </Typography>

            {row.original.isAA && <StarFilledIcon className="!size-5 shrink-0" />}

            {row.original.medicalLevel !== MedicalConditionEnum.RAS && (
              <PharmacyIcon
                className="!size-5 shrink-0"
                color={tailwindColors.green.DEFAULT}
                color2={tailwindColors.green.light.DEFAULT}
              />
            )}
          </div>
        )
      }
    },
    {
      accessorKey: 'childGender',
      header: 'Sexe',
      enableSorting: true,
      cell: ({ row }) => (
        <Typography variant="secondary-body" disabled={isDisabled(row)}>
          {row.original.childGender ? genderOneLetterMapping[row.original.childGender].label : '-'}
        </Typography>
      )
    },
    {
      accessorKey: 'childBirthDate',
      header: 'Âge',
      invertSorting: true,
      cell: ({ row }) => {
        const ageAtDeparture = EnrollmentProperties.getAgeAtDeparture(row.original)
        return (
          <Typography variant="secondary-body" disabled={isDisabled(row)}>
            {ageAtDeparture !== null ? `${ageAtDeparture} ans` : '-'}
          </Typography>
        )
      }
    },
    {
      accessorKey: 'childSchoolLevel',
      header: 'Classe',
      enableSorting: true,
      cell: ({ row }) => (
        <Typography variant="secondary-body" disabled={isDisabled(row)}>
          {row.original.childSchoolLevel
            ? schoolLevelEnumMapping[row.original.childSchoolLevel]
            : '-'}
        </Typography>
      )
    },
    {
      accessorKey: 'cseRegistration.cse.identifier',
      header: 'CSE',
      enableSorting: true,
      cell: ({ row }) => <CseIdentifierTooltip cse={row.original.cseRegistration?.cse} />
    },
    {
      accessorKey: EnrollmentProperties.getEnrollmentDates().key,
      header: 'Détails du séjour',
      cellClassName: 'whitespace-normal',
      cell: ({ row }) => {
        const dates = EnrollmentProperties.getEnrollmentDates(row.original).value
        const countryCode = EnrollmentProperties.getEnrollmentCountry(row.original).value
        const formula = EnrollmentProperties.getEnrollmentFormula(row.original).value
        const mainOption = EnrollmentProperties.getEnrollmentMainOption(row.original).value
        const options = EnrollmentProperties.getEnrollmentOptions(row.original).value
        const hoursPerWeek = EnrollmentProperties.getEnrollmentOtoHoursPerWeek(row.original).value
        if (!dates && !countryCode && !formula) return '-'
        return (
          <div className="flex flex-col gap-1 border border-grey-light p-2 rounded bg-white-hover my-1">
            <div className="flex gap-1 items-center">
              {countryCode && <CountryFlag code={countryCode} />}
              <Typography variant="label-medium" className="text-grey" disabled={isDisabled(row)}>
                {dates ? `${dates.startDate} - ${dates.endDate}` : '-'}
              </Typography>
            </div>
            {formula && (
              <Typography variant="label-medium" className="text-grey" disabled={isDisabled(row)}>
                {formula}
              </Typography>
            )}
            {mainOption && (
              <Typography variant="label-medium" className="text-grey" disabled={isDisabled(row)}>
                {mainOption}
              </Typography>
            )}
            {options && (
              <Typography variant="label-medium" className="text-grey" disabled={isDisabled(row)}>
                {options}
              </Typography>
            )}
            {hoursPerWeek !== undefined && (
              <Typography variant="label-medium" className="text-grey" disabled={isDisabled(row)}>
                {t('product.number.of.hours.per.week', { hours: hoursPerWeek })}
              </Typography>
            )}
          </div>
        )
      }
    },
    {
      accessorKey: 'mainProposition.mainTrip',
      header: 'Voyage principal',
      cellClassName: 'whitespace-normal',
      cell: ({ row }) => (
        <TripLabelCell trip={row.original.mainProposition?.mainTrip} disabled={isDisabled(row)} />
      )
    },
    {
      accessorKey: 'mainProposition.secondaryTrip',
      header: 'Voyage secondaire',
      cellClassName: 'whitespace-normal',
      cell: ({ row }) => (
        <TripLabelCell
          trip={row.original.mainProposition?.secondaryTrip}
          disabled={isDisabled(row)}
        />
      )
    },
    {
      accessorKey: 'confirmedAt',
      header: 'Inscription confirmée',
      enableSorting: true,
      cell: ({ row }) => (
        <Badge
          text={row.original.confirmedAt ? DateFormatter.DDMMYYYY(row.original.confirmedAt) : '-'}
        />
      )
    },
    {
      accessorKey: 'confirmedAt',
      header: 'Paiement',
      cell: ({ row }) => {
        const color: TailwindColorValue =
          row.original.paymentStatus === PaymentStatusEnum.FULLY_PAID ? 'green' : 'red'
        return (
          <InformationToolTip
            size="small"
            icon={
              <Badge
                color={tailwindColors[color].light.DEFAULT}
                text={<CreditCardIcon color={tailwindColors[color].DEFAULT} />}
              />
            }
          >
            <Typography variant={'label-medium'} color={tailwindColors.white.DEFAULT}>
              Paiement
            </Typography>
          </InformationToolTip>
        )
      }
    },
    {
      accessorKey: 'confirmedAt',
      header: 'Informations à compléter',
      cell: ({ row }) => (
        <EnrollmentIconsStatus
          enrollment={row.original}
          onEnrollmentUpdate={() => tableRef.current?.refetch()}
        />
      )
    },
    {
      accessorKey: 'enrollmentCenter.center.name',
      header: 'Centre',
      enableSorting: true,
      cellClassName: 'whitespace-normal',
      cell: ({ row }) => (
        <Typography variant="secondary-body" disabled={isDisabled(row)}>
          {row.original.status === ApplicationStatusEnum.AFFECTED
            ? (row.original.enrollmentCenter?.center.name ?? '-')
            : '-'}
        </Typography>
      )
    },
    {
      accessorKey: 'clientSpaceReminderByEmailSentAt',
      header: 'Relance mail',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.clientSpaceReminderByEmailSentAt ? (
          <Badge.Link
            onClick={() => reminderToggleModal('reminder', row.original.id)}
            text={`R${row.original.clientSpaceReminderByEmailsSent} - ${DateFormatter.DDMMYYYY(row.original.clientSpaceReminderByEmailSentAt)}`}
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'clientSpaceReminderByPhoneSentAt',
      header: 'Relance tel',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.clientSpaceReminderByPhoneSentAt ? (
          <Badge.Link
            onClick={() => reminderToggleModal('reminder', row.original.id)}
            text={`R${row.original.clientSpaceReminderByPhoneSent} - ${DateFormatter.DDMMYYYY(row.original.clientSpaceReminderByPhoneSentAt)}`}
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'status',
      header: 'Statut',
      enableSorting: true,
      cell: ({ row }) => (
        <Badge
          variant={applicationStatusEnumMapping[row.original.status].variant}
          text={applicationStatusEnumMapping[row.original.status].label}
        />
      )
    }
  ]

  const emails = [
    ...new Set((selectedData ?? []).flatMap((row) => [row.parent1Email, row.parent2Email]))
  ].filter((email): email is string => !!email)

  return (
    <div>
      <UserNavBar title="Inscriptions confirmées" icon={CheckListTwoIcon} />
      <div className="h-4" />
      <DynamicDataTable<EnrollmentValidatedListType, EnrollmentFilter>
        ref={tableRef}
        displayOrder
        onLoadingChange={setIsTableLoading}
        title="Toutes les inscriptions confirmées"
        // Tighter horizontal padding than the table default, and text columns allowed to wrap
        // (rows are nowrap by default), so the list fits a 23" screen
        columns={columns.map((column) => ({
          ...column,
          columnHeaderClassName: 'pl-2',
          cellClassName: cn('pl-2', column.cellClassName)
        }))}
        version="PARTIAL"
        selectable
        allowSearchParam
        extraParams={{ fields: enrollmentValidatedListFields }}
        endpoint={API.enrollments.getAll.url}
        onRowClick={(row, event) => {
          navigateWithEvent(Paths.ENROLLMENT_PROFILE_PAGE.replace(':id', row.id.toString()), event)
        }}
        filters={selectedFilter}
        filtersLength={countFilters(
          selectedFilter,
          // The default statuses are not a user filter; any other choice in the drawer is
          xor(selectedFilter?.statuses ?? [], defaultStatuses).length === 0 ? ['statuses'] : []
        )}
        onFiltersClick={() => toggleModal('filters')}
        permissions={{ delete: false }}
        actions={[
          {
            type: 'multiple',
            variant: 'primary',
            icon: BuildingPlusIcon,
            label: 'Affecter les jeunes',
            fn: (enrollments) => {
              // every selected student is listed in the popup, the ones already affected are shown as such
              if (
                enrollments.some((enrollment) => EnrollmentProperties.isAffectable(enrollment))
              ) {
                setAffectationFailures([])
                toggleModal('affectation', enrollments)
                return
              }

              toast.info(
                enrollments.length === 1
                  ? `Cette inscription est déjà affectée.`
                  : `Les inscriptions sélectionnées sont déjà affectées.`
              )
            }
          },
          {
            type: 'multiple',
            icon: EmailIcon,
            variant: 'ghost',
            label: 'Envoyer un mail groupé',
            fn: (enrollments) => toggleModal('send-email', enrollments)
          },
          {
            type: 'multiple',
            icon: BellIcon,
            variant: 'ghost',
            label: 'Relance infos espace client par email',
            fn: (enrollments) => toggleModal('confirm-email-reminder', enrollments)
          },
          {
            type: 'multiple',
            icon: BellIcon,
            variant: 'ghost',
            refetch: true,
            label: 'Relance info espace client par téléphone',
            fn: async (enrollments) => {
              await sendClientSpacePhoneReminder(enrollments.map((e) => e.id))
            }
          },
          {
            type: 'multiple',
            icon: UserCanceledIcon,
            variant: 'destructive',
            label: "Annuler l'inscription",
            disabled: (rows) => rows.length !== 1 || EnrollmentProperties.isCancelled(rows[0]),
            fn: openCancelEnrollmentModal
          }
        ]}
      />
      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onReset={resetFilters}
        onFilterChange={onFilterChange}
        onClose={() => closeModel('filters')}
        onApply={() => onSelectedFilterChange({ ...filter })}
      >
        <EnrollmentsCenterFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      {selectedData && (
        <Modal
          isOpen={modalState['send-email']}
          title={'Envoyer un email'}
          icon={EmailIcon}
          size="medium"
          hideActions
          onCancel={() => closeModel('send-email')}
        >
          <SendEmailForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={{
              emails: emails,
              subject: '',
              body: ''
            }}
            labels={[
              ...new Map(
                selectedData.flatMap((row) => {
                  const entries: [string, string][] = []
                  if (row.parent1Email)
                    entries.push([
                      row.parent1Email,
                      `${toFullName(row.parent1FirstName, row.parent1LastName)} - ${row.parent1Email}`
                    ])
                  if (row.parent2Email)
                    entries.push([
                      row.parent2Email,
                      `${toFullName(row.parent2FirstName, row.parent2LastName)} - ${row.parent2Email}`
                    ])
                  return entries
                })
              ).values()
            ]}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('send-email'),
              onConfirm: (values) => sendEmailToEnrollmentsMutation({ ...values, emails })
            })}
          />
        </Modal>
      )}

      {selectedData && (
        <Modal
          isOpen={modalState.affectation}
          title={'Affectation des jeunes'}
          icon={BuildingPlusIcon}
          size="large"
          hideActions
          isLoading={isAffectEnrollmentsPending}
          onCancel={closeAffectationModal}
        >
          <AffectEnrollmentsForm
            className={MODAL_BODY_CLASSNAME}
            enrollmentIds={affectationEnrollmentIds}
            failures={affectationFailures}
            isLoading={isAffectEnrollmentsPending}
            onCancel={closeAffectationModal}
            handleSubmit={affectEnrollmentsMutation}
          />
        </Modal>
      )}

      {reminderSelectedData && (
        <Modal
          isOpen={reminderModalState.reminder}
          title={'Historique des relances'}
          icon={BellIcon}
          hideActions
          size="medium"
          onCancel={() => reminderCloseModel('reminder')}
        >
          <RemindersHistory className={MODAL_BODY_CLASSNAME} enrollmentId={reminderSelectedData} />
        </Modal>
      )}

      {selectedData && (
        <Modal
          isOpen={modalState['confirm-email-reminder']}
          icon={BellIcon}
          size="small"
          isLoading={sendClientSpaceEmailReminderMutation.isPending}
          actionButtonsOptions={{
            cancelText: 'Annuler',
            confirmText: 'Envoyer',
            onConfirm: () =>
              sendClientSpaceEmailReminderMutation.mutate(
                selectedData.map((e) => e.id),
                { onSuccess: () => closeModel('confirm-email-reminder') }
              )
          }}
          variant="confirmation"
          title="Êtes-vous sûr de vouloir relancer le client par email concernant les informations à compléter de son Espace Personnel ?"
          onCancel={() => closeModel('confirm-email-reminder')}
        />
      )}

      {cancelSelectedEnrollment && (
        <>
          <Modal
            hideActions
            icon={UserCanceledIcon}
            isOpen={cancelModalState['cancel-enrollment']}
            onCancel={() => closeCancelModel('cancel-enrollment')}
            title={"Annuler le bulletin d'inscription"}
          >
            <CancelEnrollmentForm
              className={MODAL_BODY_CLASSNAME}
              paymentStatus={cancelSelectedEnrollment.paymentStatus}
              isConfirmed={EnrollmentProperties.isConfirmed(cancelSelectedEnrollment)}
              buttonsConfig={modalButtonConfig({
                onCancel: () => closeCancelModel('cancel-enrollment'),
                onConfirm: (data) =>
                  cancelEnrollmentMutation({ id: cancelSelectedEnrollment.id, ...data }),
                isLoading: isCancelEnrollmentPending
              })}
            />
          </Modal>
          <CancelEnrollmentNotificationModal
            enrollmentId={cancelSelectedEnrollment.id}
            isConfirmed={EnrollmentProperties.isConfirmed(cancelSelectedEnrollment)}
            isOpen={cancelModalState.notification}
            closeModal={() => closeCancelModel('notification')}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['enrollments'] })
              tableRef.current?.refetch()
            }}
          />
        </>
      )}
    </div>
  )
}

export default EnrollmentsValidatedListPage
